import { skipToken } from "@reduxjs/toolkit/query";
import {
  pickAttainmentTrendMaxPeriod,
  type AttainmentTrendMaxPeriod,
  type AttainmentTrendPeriod,
} from "@repo/app-types/entities/attainmentTrend";
import {
  getOutcomeNoun,
  isRevenueOutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import {
  getOutcomeDefinitionFromPlaybook,
  type AdminApiPlaybook,
  type PlaybookCriterionNode,
} from "@repo/app-types/entities/playbook";
import { buildTeammateDisplayNames } from "@repo/app-types/entities/teammate";
import type { TeammateWithCriteriaAverageScore } from "@repo/app-types/entities/teammatePerformanceSummary";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { SmallTabs } from "@repo/ui/Navigation/smallTabs";
import { ChartSkeleton } from "@repo/ui/Skeletons/skeletons";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router";
import {
  usePlaybookAverageScorePerTeammateForCriteriaQuery,
  usePlaybookCriteriaAttainmentTrendQuery,
  usePlaybookCriterionCoverageSecondsQuery,
  usePlaybookGlobalAverageScorePerCriteriaQuery,
  usePlaybookHighImpactCriteriaQuery,
  usePlaybookOutcomeDistributionPerCriteriaQuery,
} from "../../../api/entities/performancePlaybook.js";
import { useAllTeammatesQuery } from "../../../api/entities/teammate.js";
import { appPaths } from "../../../app/appPaths.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectCompany } from "../../company/companySlice.js";
import { buildTeamPerformancePageQueryArgs } from "../../filters/buildFilterQueryArgs.js";
import { useFilters } from "../../filters/filters.js";
import { usePlaybookFilterDefaults } from "../../filters/usePlaybookFilterDefaults.js";
import { CallVolumeByCriteriaScoreChart } from "../callVolumeByCriteriaScoreChart.js";
import { CriteriaAttainmentTrendChart } from "../criteriaAttainmentTrendChart.js";
import {
  CriteriaScoreDistributionChart,
  type ScoreRange,
} from "../criteriaScoreDistributionChart.js";
import { CriterionAverageScore } from "../criterionAverageScore.js";
import { renderGapBarWithTooltip } from "../gapBar.js";
import { OutcomeByCriteriaScoreChart } from "../outcomeByCriteriaScoreChart.js";
import { OutcomePotentialCard } from "../outcomePotentialCard.js";
import { TimeSavingsCard } from "../timeSavingsCard.js";
import { countTrendDays, TrendChart } from "../trendChart.js";
import { getTrendColorClass } from "../trendColorHelpers.js";
import { computeTrendDelta, TrendDeltaLabel } from "../trendDeltaLabel.js";

const TOP_TAB_INDEX = 0;
const BOTTOM_TAB_INDEX = 1;
const TRENDING_UP_TAB_INDEX = 2;
const TRENDING_DOWN_TAB_INDEX = 3;

// Gap (team avg − top-performer avg) below this threshold is treated as
// parity/ahead, not "savings". Shared by the ranking and per-criterion state
// so the rank pill and the card narrative agree.
const PARITY_EPSILON_SECONDS = 10;

export interface PlaybookCriterionAggregatesDashboardProps {
  playbook: AdminApiPlaybook;
  criterion: PlaybookCriterionNode;
}

export const PlaybookCriterionAggregatesDashboard: FC<
  PlaybookCriterionAggregatesDashboardProps
> = (props) => {
  const navigate = useNavigate();

  const { playbook, criterion } = props;
  const playbookId = playbook.id;
  const criterionId = criterion.id;

  const [{ teammateGroupIds }] = useFilters(["teammateGroupIds"]);

  // State for score range filtering
  const [selectedScoreRange, setSelectedScoreRange] =
    useState<ScoreRange | null>(null);

  // State for teammate sort order
  const [teammateSortTabIndex, setTeammateSortTabIndex] =
    useState(TOP_TAB_INDEX);

  const now = useNow();

  const playbookFilterDefaults = usePlaybookFilterDefaults(playbookId, "team");
  const [
    {
      dateRange,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      dateBasis,
    },
  ] = useFilters(
    [
      "dateRange",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "dateBasis",
    ],
    { playbookId, view: "team" },
    playbookFilterDefaults,
  );

  const trendDays = countTrendDays(dateRange);

  const queryArgs =
    teammateGroupIds !== undefined
      ? buildTeamPerformancePageQueryArgs(now, {
          playbookId,
          dateRange,
          teammateGroupIds,
          outcome,
          customFields,
          playbookConditions,
          signalId,
          signalMetadataFields,
          totalRecordingDuration,
          dateBasis,
        })
      : skipToken;

  // Fetch all the aggregate criteria trends.
  // TODO: Even though we should already have this in cache from `PlaybookCriteriaAggregatesTable`, this is a heavy query so it'd be better if we fetch only the trend for the currently selected criterion instead of all of them.
  const {
    data: globalAverageScorePerCriteria,
    isLoading: isGlobalAveragesLoading,
    isFetching: isGlobalAveragesFetching,
  } = usePlaybookGlobalAverageScorePerCriteriaQuery(queryArgs);

  // Get aggregated outcome criteria distribution (only if playbook has outcome)
  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);
  const {
    data: outcomeDistributionPerCriteria,
    isLoading: isOutcomeDistributionLoading,
    isFetching: isOutcomeDistributionFetching,
  } = usePlaybookOutcomeDistributionPerCriteriaQuery(
    outcomeDefinition ? queryArgs : skipToken,
  );

  // High impact criteria details (rank, calibrated net conversions, AUC,
  // total criteria count, average revenue per opp).
  const { data: highImpactCriteria, isLoading: isHighImpactCriteriaLoading } =
    usePlaybookHighImpactCriteriaQuery(
      outcomeDefinition && teammateGroupIds !== undefined
        ? {
            playbookId,
            groupIds: teammateGroupIds,
            asOfDateIso: dateRange.end?.toISO() ?? now.endOf("day").toISO(),
          }
        : skipToken,
    );

  const highImpactDetailByCriteriaId = useMemo(() => {
    if (!highImpactCriteria) {
      return undefined;
    }
    return new Map(
      highImpactCriteria.high_impact_criteria.map((entry) => [
        entry.criteria_node_id,
        entry,
      ]),
    );
  }, [highImpactCriteria]);

  // Get the noun form for outcome (e.g., "enrollment")
  const outcomeNoun = outcomeDefinition
    ? getOutcomeNoun(outcomeDefinition)
    : undefined;
  const isRevenueOutcome = outcomeDefinition
    ? isRevenueOutcomeDefinition(outcomeDefinition)
    : undefined;

  // Reset score range filter when criterion or group changes
  useEffect(() => {
    setSelectedScoreRange(null);
  }, [criterionId, teammateGroupIds]);

  // High-impact entry for the selected criterion (rank, net conversions,
  // AUC). Present whenever the criterion has correlation data — gates the
  // outcome impact / potential cards row.
  const selectedCriterionHighImpactDetail =
    highImpactDetailByCriteriaId?.get(criterionId);

  // Coverage seconds + per-criterion call counts + window scalars for the
  // time savings card. ClickHouse-only.
  const { data: coverageSeconds, isLoading: isCoverageSecondsLoading } =
    usePlaybookCriterionCoverageSecondsQuery(queryArgs);

  const isCriteriaCoverageEnabled =
    useAppSelector(selectCompany)?.settings.criteria_coverage_enabled ?? false;

  // Per-criterion ranking by weekly team hours saved (per-call savings × weekly grading volume)
  const timeSavingsRankingMap = useMemo(() => {
    const rankingMap = new Map<string, number>();
    if (!coverageSeconds) {
      return rankingMap;
    }
    const {
      criterionAverageSeconds,
      topPerformerCriterionAverageSeconds,
      criterionCallCount,
      windowDays,
    } = coverageSeconds;
    if (windowDays <= 0) {
      return rankingMap;
    }
    // All criteria with a top-performer benchmark are ranked. Parity/ahead
    // criteria (within or below the parity epsilon) get weeklyHrsSaved = 0
    // and tie at the bottom — using the same threshold as the card narrative
    // so a rank like #2 never points to a "no savings" criterion.
    const entries = Object.entries(criterionAverageSeconds)
      .flatMap(([id, avg]) => {
        const topAvg = topPerformerCriterionAverageSeconds[id];
        if (topAvg === undefined) {
          return [];
        }
        const rawDiffSec = avg - topAvg;
        const callCount = criterionCallCount[id] ?? 0;
        const weeklyHrsSaved =
          rawDiffSec > PARITY_EPSILON_SECONDS
            ? (rawDiffSec * callCount * 7) / (windowDays * 3600)
            : 0;
        return [{ id, weeklyHrsSaved }];
      })
      .sort((a, b) => {
        if (b.weeklyHrsSaved !== a.weeklyHrsSaved) {
          return b.weeklyHrsSaved - a.weeklyHrsSaved;
        }
        return a.id.localeCompare(b.id);
      });
    entries.forEach((entry, index) => {
      rankingMap.set(entry.id, index + 1);
    });
    return rankingMap;
  }, [coverageSeconds]);

  // Derived time-savings values for the selected criterion
  const timeSavings = useMemo(() => {
    if (!coverageSeconds) {
      return undefined;
    }
    const {
      criterionAverageSeconds,
      topPerformerCriterionAverageSeconds,
      criterionCallCount,
      windowDays,
    } = coverageSeconds;
    const criterionAvgSec = criterionAverageSeconds[criterionId];
    const topAvgSec = topPerformerCriterionAverageSeconds[criterionId];
    const callCount = criterionCallCount[criterionId] ?? 0;
    // Signed gap: positive = team is slower (savings to capture), negative =
    // team is faster than the company top-10 on this criterion.
    const rawDiffSec =
      criterionAvgSec !== undefined && topAvgSec !== undefined
        ? criterionAvgSec - topAvgSec
        : undefined;
    const state: "savings" | "parity" | "ahead" | "noBenchmark" =
      rawDiffSec === undefined
        ? "noBenchmark"
        : rawDiffSec > PARITY_EPSILON_SECONDS
          ? "savings"
          : rawDiffSec < -PARITY_EPSILON_SECONDS
            ? "ahead"
            : "parity";
    const secondsSavedPerCall =
      rawDiffSec !== undefined ? Math.max(0, rawDiffSec) : 0;
    const pctFaster =
      criterionAvgSec !== undefined && criterionAvgSec > 0
        ? secondsSavedPerCall / criterionAvgSec
        : undefined;
    const hrsPerWeek =
      windowDays > 0
        ? (secondsSavedPerCall * callCount * 7) / (windowDays * 3600)
        : undefined;
    return {
      state,
      secondsSavedPerCall,
      pctFaster,
      hrsPerWeek,
    };
  }, [coverageSeconds, criterionId]);

  const teammateAveragesArgs =
    queryArgs !== skipToken && criterionId !== null
      ? {
          criteriaNodeId: criterionId,
          scope: queryArgs.scope,
          filters: queryArgs.filters,
        }
      : skipToken;

  // Get the average score per teammate for the selected criterion
  const {
    data: teammateAveragesForCriteria,
    isLoading: isTeammateAveragesLoading,
    isFetching: isTeammateAveragesFetching,
  } = usePlaybookAverageScorePerTeammateForCriteriaQuery(teammateAveragesArgs);

  // Get all teammates for the criteria attainment trend chart
  const { data: teammates, isLoading: isTeammatesLoading } =
    useAllTeammatesQuery();

  // Track the chart's currently-selected period so we can widen the
  // ClickHouse query lookback only when the user actually picks a period
  // longer than 30 days.
  const [chartMaxPeriod, setChartMaxPeriod] =
    useState<AttainmentTrendMaxPeriod>("last_30_days");
  const handleChartPeriodChange = useCallback(
    (period: AttainmentTrendPeriod) => {
      setChartMaxPeriod(pickAttainmentTrendMaxPeriod([period]));
    },
    [],
  );

  // Get criteria attainment trend for the selected criterion
  const {
    data: criteriaAttainmentTrend,
    isLoading: isCriteriaAttainmentTrendLoading,
  } = usePlaybookCriteriaAttainmentTrendQuery(
    criterionId && teammateGroupIds !== undefined
      ? {
          playbookId,
          criteriaNodeId: criterionId,
          groupIds: teammateGroupIds,
          maxPeriod: chartMaxPeriod,
        }
      : skipToken,
  );

  // Helper function to calculate trend delta (most recent - oldest), ignoring
  // `null` gap days.
  const calculateTrendDelta = useCallback(
    (averageScoreSparkline: (number | null)[] | undefined): number | null => {
      const values =
        averageScoreSparkline?.filter((v): v is number => v !== null) ?? [];
      if (values.length === 0) {
        return null;
      }

      // Chronological order: oldest is the first non-null value, newest is the last.
      const newest = values[values.length - 1];
      const oldest = values[0];
      if (newest === undefined || oldest === undefined) {
        return null;
      }
      return newest - oldest;
    },
    [],
  );

  // Helper function to sort teammates based on selected sort order
  const sortTeammatesByTabIndex = useCallback(
    (
      teammates: TeammateWithCriteriaAverageScore[],
      tabIndex: number,
    ): TeammateWithCriteriaAverageScore[] => {
      return teammates.toSorted((a, b) => {
        switch (tabIndex) {
          // Sort by top
          case TOP_TAB_INDEX:
            return b.average_score - a.average_score;
          // Sort by bottom
          case BOTTOM_TAB_INDEX:
            return a.average_score - b.average_score;
          // Sort by trending up (highest delta first)
          case TRENDING_UP_TAB_INDEX: {
            const deltaA = calculateTrendDelta(a.average_score_sparkline);
            const deltaB = calculateTrendDelta(b.average_score_sparkline);
            // Handle null cases: put teammates without trend data at the end
            if (deltaA === null && deltaB === null) {
              return 0;
            }
            if (deltaA === null) {
              return 1;
            }
            if (deltaB === null) {
              return -1;
            }
            return deltaB - deltaA;
          }
          // Sort by trending down (lowest delta first)
          case TRENDING_DOWN_TAB_INDEX: {
            const deltaA = calculateTrendDelta(a.average_score_sparkline);
            const deltaB = calculateTrendDelta(b.average_score_sparkline);
            // Handle null cases: put teammates without trend data at the end
            if (deltaA === null && deltaB === null) {
              return 0;
            }
            if (deltaA === null) {
              return 1;
            }
            if (deltaB === null) {
              return -1;
            }
            return deltaA - deltaB;
          }
          default:
            // Use top sorting by default
            return b.average_score - a.average_score;
        }
      });
    },
    [calculateTrendDelta],
  );

  // Filter and sort teammates based on selected score range and sort order
  const filteredTeammatesForCriteria = useMemo(() => {
    if (!teammateAveragesForCriteria) {
      return teammateAveragesForCriteria;
    }

    // Filter by distribution teammate IDs if selected
    const filteredTeammateAveragesForCriteria = selectedScoreRange
      ? teammateAveragesForCriteria.filter((teammate) =>
          selectedScoreRange.teammateIds.includes(teammate.id),
        )
      : teammateAveragesForCriteria;

    // Sort
    return sortTeammatesByTabIndex(
      filteredTeammateAveragesForCriteria,
      teammateSortTabIndex,
    );
  }, [
    teammateAveragesForCriteria,
    selectedScoreRange,
    teammateSortTabIndex,
    sortTeammatesByTabIndex,
  ]);

  const aggregateTrendForSelectedCriterion = useMemo(() => {
    const data = globalAverageScorePerCriteria?.find(
      (item) => item.criteria_node_id === criterionId,
    );

    if (!data?.global_average_score_sparkline) {
      return null;
    }

    return {
      currentAverage: data.global_average_score,
      trend: data.global_average_score_sparkline,
      deltaFromOldest: computeTrendDelta(data.global_average_score_sparkline),
    };
  }, [criterionId, globalAverageScorePerCriteria]);

  const isAggregateTrendLoading =
    isGlobalAveragesLoading || isGlobalAveragesFetching;

  // Memoize columns to prevent re-creation
  const columns = useMemo(() => {
    if (!criterion) {
      return [];
    }

    const buildColumns = (
      gradingValueType: PlaybookCriterionNode["grading_value_type"],
    ): readonly EntityTableColumnDefinition<TeammateWithCriteriaAverageScore>[] => [
      {
        id: "teammate",
        header: "Teammate",
        isRowHeader: true,
        renderCell: (row) => {
          const { primaryDisplayName, secondaryDisplayName } =
            buildTeammateDisplayNames(row);

          const averageScore = row.average_score;
          const medianAverageScore = row.benchmark_median_score ?? undefined;
          const averageScoreTrend = row.average_score_sparkline;

          const renderCriterionGapBar = () => {
            if (
              averageScore === undefined ||
              medianAverageScore === undefined
            ) {
              return null;
            }

            const isYesNoGradingValueType = gradingValueType === "yes_no";

            const score = isYesNoGradingValueType
              ? Math.round(averageScore * 100)
              : parseFloat(averageScore.toFixed(1));

            const benchmark = isYesNoGradingValueType
              ? Math.round(medianAverageScore * 100)
              : parseFloat(medianAverageScore.toFixed(1));

            const tooltipContentPrefix = isYesNoGradingValueType
              ? `${score}%`
              : `${score}`;

            const range: [number, number] = isYesNoGradingValueType
              ? [0, 100]
              : [1, 5];

            return renderGapBarWithTooltip(
              tooltipContentPrefix,
              score,
              benchmark,
              { range },
            );
          };

          const renderAverageScoreTrend = () => {
            if (!averageScoreTrend) {
              return null;
            }

            const delta = computeTrendDelta(averageScoreTrend);

            return (
              <TrendDeltaLabel
                delta={delta}
                gradingValueType={criterion.grading_value_type}
                trendDays={trendDays}
                size={Size.SMALL}
              />
            );
          };

          return (
            <div className="flex items-center justify-between gap-3">
              <div>
                {primaryDisplayName}
                {secondaryDisplayName ? (
                  <div className="truncate text-gray-600">
                    {secondaryDisplayName}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-3">
                <CriterionAverageScore
                  valueType={gradingValueType}
                  value={averageScore}
                />
                {renderCriterionGapBar()}
                {renderAverageScoreTrend()}
              </div>
            </div>
          );
        },
      },
    ];

    return buildColumns(criterion.grading_value_type);
  }, [criterion, trendDays]);

  const averageScoreCard = (
    <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md bg-white px-4 pt-3 pb-4 ring-1 ring-black/5">
      <Label size={Size.SMALL} className="mb-2">
        Average score
      </Label>

      {isAggregateTrendLoading ? (
        <div className="h-24 w-full animate-pulse rounded-sm bg-gray-200" />
      ) : aggregateTrendForSelectedCriterion ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <CriterionAverageScore
              valueType={criterion.grading_value_type}
              value={aggregateTrendForSelectedCriterion.currentAverage}
            />
            {aggregateTrendForSelectedCriterion.deltaFromOldest !== null ? (
              <TrendDeltaLabel
                delta={aggregateTrendForSelectedCriterion.deltaFromOldest}
                gradingValueType={criterion.grading_value_type}
                trendDays={trendDays}
              />
            ) : null}
          </div>
          {/* Stacked in a single column, the card has no row height to fill,
              so the chart needs an explicit one. */}
          <div className="h-32 min-w-0">
            <TrendChart
              className="h-full w-full"
              showsXAxisLabels
              showsYAxisLabels
              minYRange={criterion.grading_value_type === "yes_no" ? 0.01 : 0.1}
              getColorClassName={(deltaY) =>
                getTrendColorClass(deltaY, criterion.grading_value_type)
              }
              lines={[
                {
                  key: "aggregateTrend",
                  data: aggregateTrendForSelectedCriterion.trend,
                },
              ]}
              renderValue={(value) =>
                criterion.grading_value_type === "yes_no"
                  ? `${Math.round(value * 100)}%`
                  : value.toFixed(1)
              }
            />
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-600">No trend data available.</p>
      )}
    </div>
  );

  // The dashboard lives in the detail column of the playbook page's split
  // view, so cards stack in a single column rather than pairing up, and the
  // headline average score leads.
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {averageScoreCard}

      {outcomeDefinition ? (
        <>
          {selectedCriterionHighImpactDetail ? (
            <div className="grid grid-cols-1 gap-4">
              <>
                {isHighImpactCriteriaLoading ? (
                  <div className="min-w-0 overflow-hidden rounded-md bg-white px-4 pt-3 pb-4 ring-1 ring-black/5">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-2">
                        <div className="h-4 w-40 animate-pulse rounded-sm bg-gray-200" />
                        <div className="h-6 w-32 animate-pulse rounded-sm bg-gray-200" />
                      </div>
                      <div className="h-8 w-full animate-pulse rounded-sm bg-gray-200" />
                    </div>
                  </div>
                ) : (
                  <OutcomePotentialCard
                    rank={selectedCriterionHighImpactDetail.rank}
                    totalCriteria={
                      highImpactCriteria?.total_criteria_count ?? 0
                    }
                    auc={selectedCriterionHighImpactDetail.auc}
                    netConversions={Math.round(
                      selectedCriterionHighImpactDetail.net_conversions,
                    )}
                    averageRevenuePerOpp={
                      highImpactCriteria?.average_revenue_per_opp ?? undefined
                    }
                    outcomeNoun={outcomeNoun}
                    outcomePluralNoun={outcomeDefinition?.plural_noun}
                    isRevenueOutcome={isRevenueOutcome}
                  />
                )}
                {isCriteriaCoverageEnabled ? (
                  isCoverageSecondsLoading ? (
                    <div className="min-w-0 overflow-hidden rounded-md bg-white px-4 pt-3 pb-4 ring-1 ring-black/5">
                      <div className="space-y-2">
                        <div className="h-4 w-32 animate-pulse rounded-sm bg-gray-200" />
                        <div className="h-6 w-20 animate-pulse rounded-sm bg-gray-200" />
                        <div className="h-8 w-full animate-pulse rounded-sm bg-gray-200" />
                      </div>
                    </div>
                  ) : (
                    <TimeSavingsCard
                      rank={timeSavingsRankingMap.get(criterionId)}
                      totalCriteria={highImpactCriteria?.total_criteria_count}
                      secondsSavedPerCall={timeSavings?.secondsSavedPerCall}
                      pctFaster={timeSavings?.pctFaster}
                      hrsPerWeek={timeSavings?.hrsPerWeek}
                      state={timeSavings?.state}
                    />
                  )
                ) : null}
              </>
            </div>
          ) : null}

          {/* Outcome + Call Volume Charts */}
          <div className="flex flex-col gap-4">
            {/* Outcome Chart */}
            <div className="min-w-0 overflow-hidden rounded-md bg-white px-4 pt-3 pb-4 ring-1 ring-black/5">
              {isOutcomeDistributionLoading || isOutcomeDistributionFetching ? (
                <div className="space-y-2">
                  <div className="h-4 w-40 animate-pulse rounded-sm bg-gray-200" />
                  <div className="h-24 w-full animate-pulse rounded-sm bg-gray-200" />
                </div>
              ) : outcomeDistributionPerCriteria ? (
                <OutcomeByCriteriaScoreChart
                  outcomeDefinition={outcomeDefinition}
                  criterionDistribution={
                    outcomeDistributionPerCriteria[criterion.id]
                  }
                  gradingValueType={criterion.grading_value_type}
                />
              ) : null}
            </div>

            {/* Call Volume Chart */}
            <div className="min-w-0 overflow-hidden rounded-md bg-white px-4 pt-3 pb-4 ring-1 ring-black/5">
              {isOutcomeDistributionLoading || isOutcomeDistributionFetching ? (
                <div className="space-y-2">
                  <div className="h-4 w-40 animate-pulse rounded-sm bg-gray-200" />
                  <div className="h-24 w-full animate-pulse rounded-sm bg-gray-200" />
                </div>
              ) : outcomeDistributionPerCriteria ? (
                <CallVolumeByCriteriaScoreChart
                  criterionDistribution={
                    outcomeDistributionPerCriteria[criterion.id]
                  }
                  gradingValueType={criterion.grading_value_type}
                />
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      {/* Team distribution */}
      <div className="min-w-0 overflow-hidden rounded-md bg-white px-4 pt-3 pb-4 ring-1 ring-black/5">
        <Label size={Size.SMALL} className="mb-2">
          Team distribution
        </Label>
        {isAggregateTrendLoading ? (
          <div className="h-24 w-full animate-pulse rounded-sm bg-gray-200" />
        ) : (
          <CriteriaScoreDistributionChart
            teammates={
              teammateAveragesForCriteria
                ?.filter((item) => item.criteria_node_id === criterionId)
                .map((item) => ({
                  teammateId: item.id,
                  score: item.average_score,
                })) ?? []
            }
            gradingValueType={criterion.grading_value_type}
            selectedRange={selectedScoreRange}
            onRangeSelect={(range) => {
              setSelectedScoreRange(range);
            }}
          />
        )}
      </div>

      {/* Criteria Attainment Trend Chart */}
      {isTeammatesLoading || isCriteriaAttainmentTrendLoading ? (
        <div className="min-w-0 overflow-hidden rounded-md bg-white px-4 pt-3 pb-4 ring-1 ring-black/5">
          <Label size={Size.SMALL} className="mb-2">
            Criteria attainment trend
          </Label>
          <div className="h-80">
            <ChartSkeleton />
          </div>
        </div>
      ) : teammates &&
        criteriaAttainmentTrend &&
        Object.keys(criteriaAttainmentTrend).length > 0 ? (
        <CriteriaAttainmentTrendChart
          attainmentTrend={criteriaAttainmentTrend}
          teammates={teammates}
          playbookId={playbookId}
          criterionId={criterion.id}
          gradingValueType={criterion.grading_value_type}
          onSelectedPeriodChange={handleChartPeriodChange}
        />
      ) : null}

      {/* Sort tabs and the teammates table read as one unit, so they share a
          card. The nav's own rounding/shadow is dropped to sit flush in it. */}
      <div className="overflow-hidden rounded-lg border border-border-light bg-white">
        <div className="border-b border-border-light [&_nav]:rounded-none [&_nav]:shadow-none">
          <SmallTabs
            tabs={["Top", "Bottom", "Trending up", "Trending down"]}
            selectedTabIndex={teammateSortTabIndex}
            onSelectTabIndex={setTeammateSortTabIndex}
          />
        </div>

        <EntityTable
          aria-label="Criterion teammate averages"
          size={Size.SMALL}
          columns={columns}
          entities={
            filteredTeammatesForCriteria?.filter(
              (item) => item.criteria_node_id === criterionId,
            ) ?? []
          }
          isLoading={isTeammateAveragesLoading || isTeammateAveragesFetching}
          emptyMessage={
            selectedScoreRange
              ? "No teammates in this score range."
              : "No teammates found."
          }
          onEntityAction={(row) =>
            navigate(
              appPaths.playbookCriterionTeammate.build({
                playbookId,
                criterionId: criterion.id,
                teammateId: row.id,
              }),
            )
          }
        />
      </div>
    </div>
  );
};
